import { useState, useEffect } from 'react'
import { useNavigate, Link as RouterLink } from 'react-router-dom'
import {
	Box,
	Card,
	CardContent,
	TextField,
	Button,
	Typography,
	Alert,
	Link,
} from '@mui/material'
import { useAuth } from '../contexts/auth-context'
import { supabase } from '../lib/supabase'

export function ResetPasswordPage() {
	const [password, setPassword] = useState('')
	const [confirmPassword, setConfirmPassword] = useState('')
	const [error, setError] = useState(null)
	const [success, setSuccess] = useState(false)
	const [isSubmitting, setIsSubmitting] = useState(false)
	const [recoveryReady, setRecoveryReady] = useState(false)
	const { clearPasswordReset, pendingPasswordReset } = useAuth()
	const navigate = useNavigate()

	useEffect(() => {
		const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
			if (event === 'PASSWORD_RECOVERY') {
				setRecoveryReady(true)
			}
		})

		// If the auth context already flagged a pending reset, we're ready
		if (pendingPasswordReset) {
			setRecoveryReady(true)
		}

		// Also check if we already have a session (user clicked link and session was restored)
		supabase.auth.getSession().then(({ data: { session } }) => {
			if (session) {
				setRecoveryReady(true)
			}
		})

		return () => subscription.unsubscribe()
	}, [pendingPasswordReset])

	const handleSubmit = async (e) => {
		e.preventDefault()
		setError(null)

		if (password !== confirmPassword) {
			setError('Passwords do not match')
			return
		}

		if (password.length < 6) {
			setError('Password must be at least 6 characters long')
			return
		}

		setIsSubmitting(true)

		try {
			const { error: updateError } = await supabase.auth.updateUser({
				password,
			})

			if (updateError) {
				setError(updateError.message)
				setIsSubmitting(false)
				return
			}

			setSuccess(true)
			// Clear the recovery flag so the layout restores authenticated UI,
			// then navigate to home — the user is already authenticated
			setTimeout(() => {
				clearPasswordReset()
				navigate('/', { replace: true })
			}, 2000)
		} catch (err) {
			setError(err.message || 'An error occurred')
			setIsSubmitting(false)
		}
	}

	if (!recoveryReady) {
		return (
			<Box
				mt={20}
				paddingTop='5rem'
				display="flex"
				justifyContent="center"
				alignItems="center"
				minHeight="60vh"
				maxWidth="sm"
				margin='auto'
			>
				<Card
					elevation={4}
					sx={{
						borderRadius: 3,
						px: 6,
						py: 8,
						width: '100%',
						textAlign: 'center',
					}}
				>
					<CardContent>
						<Typography variant="h4" fontWeight="bold" gutterBottom>
							Reset Password
						</Typography>
						<Typography variant="body1" color="text.secondary" mb={4}>
							Verifying your reset link...
						</Typography>
						<Box sx={{ mt: 2, textAlign: 'center' }}>
							<Typography variant="body2" color="text.secondary">
								If this page doesn&apos;t load, your reset link may have expired.{' '}
								<Link component={RouterLink} to="/forgot-password" underline="hover">
									Request a new one
								</Link>
							</Typography>
						</Box>
					</CardContent>
				</Card>
			</Box>
		)
	}

	return (
		<Box
			mt={20}
			paddingTop='5rem'
			display="flex"
			justifyContent="center"
			alignItems="center"
			minHeight="60vh"
			maxWidth="sm"
			margin='auto'
		>
			<Card
				elevation={4}
				sx={{
					borderRadius: 3,
					px: 6,
					py: 8,
					width: '100%',
					textAlign: 'center',
				}}
			>
				<CardContent>
					<Typography variant="h4" fontWeight="bold" gutterBottom>
						Set New Password
					</Typography>
					<Typography variant="body1" color="text.secondary" mb={4}>
						Enter your new password below.
					</Typography>
					{success ? (
						<Alert severity="success" sx={{ mt: 2 }}>
							Password updated successfully! Redirecting...
						</Alert>
					) : (
						<Box component="form" onSubmit={handleSubmit} sx={{ textAlign: 'left' }}>
							<TextField
								fullWidth
								id="new-password"
								label="New Password"
								type="password"
								placeholder="Enter your new password"
								value={password}
								onChange={(e) => setPassword(e.target.value)}
								required
								autoComplete="new-password"
								margin="normal"
								helperText="Minimum 6 characters"
							/>
							<TextField
								fullWidth
								id="confirm-new-password"
								label="Confirm New Password"
								type="password"
								placeholder="Confirm your new password"
								value={confirmPassword}
								onChange={(e) => setConfirmPassword(e.target.value)}
								required
								autoComplete="new-password"
								margin="normal"
							/>
							{error && (
								<Alert severity="error" sx={{ mt: 2 }}>
									{error}
								</Alert>
							)}
							<Button
								type="submit"
								variant="contained"
								fullWidth
								disabled={isSubmitting}
								sx={{ mt: 3, py: 1.5 }}
							>
								{isSubmitting ? 'Updating...' : 'Update Password'}
							</Button>
						</Box>
					)}
				</CardContent>
			</Card>
		</Box>
	)
}
